import React, { PropsWithChildren } from 'react';
import { Disclosure as HeadlessDisclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { classNames } from '../utils/classNames';

// A one-line toggle that opens a block of content under it. The chevron points right when the
// block is closed and down when it is open. The button toggles on a click, on Enter and on Space.
// `small` is the size for a popup or a table cell (text-xs), `medium` the size for a page (text-sm).
export type DisclosureSize = 'small' | 'medium';

type Props = {
	label: string,
	defaultOpen?: boolean,
	size?: DisclosureSize,
	// The content sits under the label, past the chevron (a 16 px indent).
	indent?: boolean,
	disabled?: boolean,
	className?: string,
	onToggle?: (open: boolean) => void,
};

const SIZE: Record<DisclosureSize, { button: string, icon: string, panel: string }> = {
	small: { button: 'gap-x-1 text-xs leading-4', icon: 'h-3 w-3', panel: 'mt-1.5' },
	medium: { button: 'gap-x-1.5 text-sm leading-5', icon: 'h-4 w-4', panel: 'mt-2' },
};

const Disclosure: React.FunctionComponent<PropsWithChildren<Props>> = ({
	label,
	defaultOpen = false,
	size = 'small',
	indent = true,
	disabled = false,
	className,
	onToggle,
	children,
}) => {
	const sizes = SIZE[size];
	return <HeadlessDisclosure as="div" defaultOpen={defaultOpen} className={className ?? ''}>
		{({ open }) => <>
			<DisclosureButton disabled={disabled}
				onClick={() => { if (onToggle !== undefined) onToggle(!open); }}
				className={classNames(
					'inline-flex items-center rounded-sm font-medium text-gray-500 hover:text-blue-500',
					'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600',
					'disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:text-gray-300',
					sizes.button)}>
				{open
					? <ChevronDownIcon className={classNames(sizes.icon, 'shrink-0')} aria-hidden="true" />
					: <ChevronRightIcon className={classNames(sizes.icon, 'shrink-0')} aria-hidden="true" />}
				<span>{label}</span>
			</DisclosureButton>
			<DisclosurePanel className={classNames(sizes.panel, indent ? 'pl-4' : '')}>
				{children}
			</DisclosurePanel>
		</>}
	</HeadlessDisclosure>;
};

export default Disclosure;
